import React, { useState } from 'react';
import { Popconfirm } from 'antd';
import {
    useGetMyPortalProjectsQuery,
    useDeletePortalProjectMutation,
} from '../../../redux/api/compassPortal';
import { StatusPill, overallStatus, errMsg } from './bits';
import CompassLoader from './CompassLoader';
import CreateProjectModal from '../modals/CreateProjectModal';

// Screen 1 — Projects list. Entry point that sits before everything; select a
// project to open its workspace. Admins can create/delete a project (the backend
// enforces the admin claim regardless). Reuses the app's existing antd
// CreateProjectModal so project creation stays identical to the old portal.

export default function ProjectsList({ onOpen, isAdmin }) {
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const { data: projects, error: loadError } = useGetMyPortalProjectsQuery();
    const [deleteProject, { isLoading: deleting }] = useDeletePortalProjectMutation();
    const [busyId, setBusyId] = useState(null);

    const del = async (p) => {
        setBusyId(p.project_id); setError(null);
        try { await deleteProject({ projectId: p.project_id }).unwrap(); }
        catch (e) { setError(`Couldn't delete — ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };

    if (loadError && !projects) return <div className="cp-empty">Couldn't load projects — {errMsg(loadError)}</div>;
    if (!projects) return <CompassLoader />;

    return (
        <>
            <div className="cp-toolbar">
                <p className="cp-section-label">{projects.length} project{projects.length === 1 ? '' : 's'}</p>
                {isAdmin && (
                    <button className="cp-btn cp-btn--primary" onClick={() => setCreating(true)}>+ New project</button>
                )}
            </div>
            {error && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{error}</p>}

            {projects.length === 0 ? (
                <div className="cp-empty">No projects yet.{isAdmin ? ' Create one to get started.' : ''}</div>
            ) : (
                <>
                    <div className="cp-listhead">
                        <span>Project</span><span>Status</span><span>Delivery</span><span />
                    </div>
                    <div className="cp-projects">
                        {projects.map((p) => (
                            <div key={p.project_id} className="cp-proj-row" role="button" tabIndex={0}
                                onClick={() => onOpen(p)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(p); } }}>
                                <span>
                                    <span className="cp-proj-name">{p.project_name}</span>
                                    <span className="cp-proj-client">{p.client_name}</span>
                                </span>
                                <span><StatusPill status={overallStatus(p.stages)} /></span>
                                <span className="cp-due">{p.delivery_date || '—'}</span>
                                {isAdmin ? (
                                    <Popconfirm title={`Delete "${p.project_name}"?`}
                                        description="This removes the project and its files and can't be undone."
                                        okText="Delete" okButtonProps={{ danger: true }}
                                        onPopupClick={(e) => e.stopPropagation()}
                                        onConfirm={(e) => { e?.stopPropagation?.(); del(p); }}
                                        onCancel={(e) => e?.stopPropagation?.()}>
                                        <button className="cp-proj-del" aria-label={`Delete ${p.project_name}`} title="Delete project"
                                            disabled={deleting && busyId === p.project_id}
                                            onClick={(e) => e.stopPropagation()}>
                                            {deleting && busyId === p.project_id ? '…' : '✕'}
                                        </button>
                                    </Popconfirm>
                                ) : <span className="cp-proj-arrow">→</span>}
                            </div>
                        ))}
                    </div>
                </>
            )}

            <CreateProjectModal open={creating} onClose={() => setCreating(false)} />
        </>
    );
}
